// src/features/channelManager/pages/ChannelManagerHome.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Card, Input, Typography, message, Tag, Select, Tooltip } from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { Activity, CheckCircle, AlertTriangle, Filter } from "react-feather";
import { useNavigate } from "react-router-dom";

import ChannelTable from "./components/ChannelTable";
import { channelsApi } from "./api/channelsApi";
import ChannexIframeModal from "./components/ChannexIframeModal";

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "connected", label: "Connected" },
  { value: "not_connected", label: "Not Connected" },
  { value: "issues", label: "Needs attention" },
  { value: "disabled", label: "Disabled" },
];

// Optional: internal code -> Channex filter code used by iframe (best effort)
const OTA_CODE_MAP = {
  booking: "BDC",
  expedia: "EXP",
  agoda: "AGO",
  airbnb: "ABB",
};

export default function ChannelManagerHome() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({}); // { [code]: { sync?:bool, active?:bool, mapping?:bool, reconcile?:bool } }
  const [channels, setChannels] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");

  // Mapping iframe
  const [iframeOpen, setIframeOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");

  const setBusy = useCallback((code, key, val) => {
    setActionLoading((p) => ({
      ...p,
      [code]: { ...(p[code] || {}), [key]: val },
    }));
  }, []);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await channelsApi.listCompanyChannels?.({ search });
      setChannels(resp?.data?.data || []);
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to load channels");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const providerOptions = useMemo(() => {
    const set = new Set();
    for (const c of channels || []) {
      if (c?.api_provider) set.add(c.api_provider);
    }
    const list = Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
    return [{ value: "all", label: "All providers" }, ...list.map((p) => ({ value: p, label: p }))];
  }, [channels]);

  const filtered = useMemo(() => {
    const s = (search || "").trim().toLowerCase();

    return (channels || []).filter((c) => {
      const matchSearch =
        !s ||
        (c.name || "").toLowerCase().includes(s) ||
        (c.code || "").toLowerCase().includes(s) ||
        (c.api_provider || "").toLowerCase().includes(s);

      const connected = !!c.is_connected;
      // IMPORTANT: treat undefined as "not connected" (not disabled)
      const active = c.is_active === undefined ? true : c.is_active !== false;
      const hasIssue = !!c.error_message || String(c.last_sync_status || "").toLowerCase() === "failed";

      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "connected" && connected && active) ||
        (statusFilter === "not_connected" && !connected) ||
        (statusFilter === "issues" && hasIssue) ||
        (statusFilter === "disabled" && connected && !active);

      const matchProvider = providerFilter === "all" || String(c.api_provider || "") === providerFilter;

      return matchSearch && matchStatus && matchProvider;
    });
  }, [channels, search, statusFilter, providerFilter]);

  const kpis = useMemo(() => {
    const all = filtered || [];
    const connected = all.filter((c) => !!c.is_connected).length;
    const disabled = all.filter((c) => !!c.is_connected && c?.is_active === false).length;
    const issues = all.filter(
      (c) => !!c.error_message || String(c.last_sync_status || "").toLowerCase() === "failed"
    ).length;
    return { total: all.length, connected, disabled, issues };
  }, [filtered]);

  // ✅ Sync Now
  const onSync = async (record) => {
    const code = record.code;
    setBusy(code, "sync", true);
    try {
      await channelsApi.syncNow({ channel_code: code, type: "bookings" });
      message.success(`Sync triggered for ${record.name || record.code}`);
      await loadChannels();
    } catch (e) {
      message.error(e.response?.data?.message || "Sync failed");
    } finally {
      setBusy(code, "sync", false);
    }
  };

  /**
   * ✅ Disable/Enable is the correct semantic for Channex.
   * - Disconnect = setActive(false) (backend should deactivate in Channex)
   * - Reconnect = setActive(true)  (backend should activate in Channex)
   *
   * This ensures we do NOT push user back into setup flow when entry exists.
   */
  const onSetActive = async (record, nextActive) => {
    const code = record.code;
    setBusy(code, "active", true);
    try {
      const resp = await channelsApi.setActive({ channel_code: code, is_active: !!nextActive });

      // If remote failed because channex_channel_id missing, give a premium, actionable message
      const remoteOk = resp?.data?.remote?.success !== false;
      if (!remoteOk) {
        message.warning(
          resp?.data?.remote?.message ||
            "Updated locally. Remote Channex update needs 'Finish setup' once to link channel id."
        );
      } else {
        message.success(`${record.name || record.code} ${nextActive ? "reconnected" : "disabled"}`);
      }

      await loadChannels();
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to update channel status");
    } finally {
      setBusy(code, "active", false);
    }
  };

  // ✅ Finish setup (reconcile channex_channel_id)
  const onReconcileChannel = async (record) => {
    const code = record.code;
    setBusy(code, "reconcile", true);
    try {
      if (!record.property_id) {
        message.error("Missing property_id. Connect first.");
        return;
      }
      await channelsApi.reconcile({ property_id: record.property_id, channel_code: code });
      message.success(`${record.name || record.code} linked with Channex (channel id saved)`);
      await loadChannels();
    } catch (e) {
      message.error(e.response?.data?.message || "Finish setup failed");
    } finally {
      setBusy(code, "reconcile", false);
    }
  };

  // ✅ Open Mapping (Channex embedded)
  const onOpenMapping = async (record) => {
    const code = record.code;
    setBusy(code, "mapping", true);
    try {
      if (!record.property_id) return message.error("Connect the channel first to open mapping.");

      const channexCode = OTA_CODE_MAP[record.code] || record.code;

const resp = await channelsApi.iframeToken({
  provider_code: "channex",
  redirect_to: "/channels",
  channels: channexCode,
});

      setIframeUrl(resp.data?.iframe_url || "");
      setIframeOpen(true);
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to open mapping");
    } finally {
      setBusy(code, "mapping", false);
    }
  };

  // ✅ Connect channel (new connect)
  const onConnectChannel = (code) => {
    navigate(`/channel-manager/setup?channel=${encodeURIComponent(code)}`);
  };

  const connectedFirst = useMemo(() => {
    const list = [...(filtered || [])];
    list.sort(
      (a, b) =>
        Number(!!b.is_connected) - Number(!!a.is_connected) ||
        Number((b.is_active === undefined ? true : b.is_active !== false)) -
          Number((a.is_active === undefined ? true : a.is_active !== false))
    );
    return list;
  }, [filtered]);

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        {/* Header */}
        <div className="page-header page-top-icons" style={{ marginBottom: 16 }}>
          <div className="row align-items-center">
            <div className="col-md-5">
              <Title level={3} style={{ margin: 0 }}>
                Channel Manager
              </Title>
              <Text type="secondary">
                Connect OTAs, manage mapping, and control <b>Rates & Availability</b> from one place.
              </Text>

              {/* KPI pills */}
              <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Tag
                  color="green"
                  style={{
                    borderRadius: 999,
                    padding: "4px 10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginInlineStart: 0,
                  }}
                >
                  <CheckCircle size={14} /> Connected: <b>{kpis.connected}</b>
                </Tag>

                <Tag
                  color={kpis.disabled ? "default" : "blue"}
                  style={{
                    borderRadius: 999,
                    padding: "4px 10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginInlineStart: 0,
                  }}
                >
                  <Activity size={14} />
                  Disabled: <b>{kpis.disabled}</b>
                </Tag>

                <Tag
                  color={kpis.issues ? "red" : "blue"}
                  style={{
                    borderRadius: 999,
                    padding: "4px 10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginInlineStart: 0,
                  }}
                >
                  {kpis.issues ? <AlertTriangle size={14} /> : <Activity size={14} />}
                  Attention: <b>{kpis.issues}</b>
                </Tag>

                <Tag style={{ borderRadius: 999, padding: "4px 10px", marginInlineStart: 0 }}>
                  Total: <b>{kpis.total}</b>
                </Tag>
              </div>
            </div>

            <div className="col-md-7">
              <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">
                <Input.Search
                  allowClear
                  placeholder="Search channels… (Booking.com, Airbnb, Expedia)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSearch={loadChannels}
                  style={{ width: 320 }}
                />

                <Tooltip title="Filter list">
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={STATUS_OPTIONS}
                    style={{ width: 170 }}
                    suffixIcon={<Filter size={16} />}
                  />
                </Tooltip>

                <Select
                  value={providerFilter}
                  onChange={setProviderFilter}
                  options={providerOptions}
                  style={{ width: 180 }}
                />

                <Button icon={<ReloadOutlined />} loading={loading} onClick={loadChannels}>
                  Refresh
                </Button>

                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/channel-manager/setup")}>
                  Connect Channel
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* List */}
        <Card style={{ borderRadius: 16 }}>
          <ChannelTable
            channels={connectedFirst}
            loading={loading}
            actionLoading={actionLoading} // ✅ NEW: per-row loading
            onSync={onSync}
            onOpenMapping={onOpenMapping}
            onConnectChannel={onConnectChannel}
            onToggleActive={onSetActive} // ✅ use this for disable + reconnect
            onReconcileChannel={onReconcileChannel}
          />
        </Card>

        <ChannexIframeModal
          open={iframeOpen}
          onClose={() => setIframeOpen(false)}
          url={iframeUrl}
          title="Channex — Mapping & Activation"
        />

        {/* tiny style polish (safe + scoped) */}
        <style>{`
          .ns-row-connected td{
            background: linear-gradient(90deg, rgba(16,185,129,0.08), rgba(16,185,129,0.03)) !important;
          }
          .ns-row-connected td:first-child{
            position: relative;
          }
          .ns-row-connected td:first-child::before{
            content:"";
            position:absolute;
            left:0;
            top:0;
            bottom:0;
            width:4px;
            background: rgba(16,185,129,0.9);
            border-radius: 8px;
          }
        `}</style>
      </div>
    </div>
  );
}
